import React, { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { getUserFromLocalStorage } from '../../utilities/user';
import { apiFetchPartnerPayouts } from '../../api/index.js';
import {
    colors,
    font,
    text,
    card,
    cardBody,
    grid,
    inp,
    button,
    pill,
    ugx,
} from './partnerTheme';
import PartnerHelp from './partnerHelp';
import AppSelect from "../../components/ui/AppSelect";

const PAGE_SIZE = 20;

/**
 * The statuses below are the PartnerPayout schema enum, in the order a payout
 * actually moves through them. They are listed explicitly rather than derived
 * from the rows on screen, because a partner who has no failed payout yet
 * should still be able to ask "do I have any failed payouts?" and get an
 * answer instead of an option that quietly does not exist.
 */
const STATUSES = [
    { value: '', label: 'All' },
    { value: 'draft', label: 'Draft' },
    { value: 'pending', label: 'Pending' },
    { value: 'approved', label: 'Approved' },
    { value: 'processing', label: 'Processing' },
    { value: 'completed', label: 'Completed' },
    { value: 'failed', label: 'Failed' },
    { value: 'cancelled', label: 'Cancelled' },
];

/** Money has left Lusuku only on completed. Everything before that is a promise. */
const statusTone = (status) => {
    if (status === 'completed') return 'good';
    if (status === 'failed' || status === 'cancelled') return 'bad';
    if (status === 'processing' || status === 'approved') return 'info';
    if (status === 'pending') return 'warn';
    return 'muted';
};

const formatDate = (value) => {
    if (!value) return 'Not set';
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return 'Not set';
    return d.toLocaleDateString('en-UG', { day: 'numeric', month: 'short', year: 'numeric' });
};

/**
 * A payout can be sent by bank, mobile money or cheque, and each of those
 * keeps its destination in a different key. Show the one that was actually
 * filled in; a partner chasing a missing payment needs to see which account it
 * was aimed at, not just that "mobile money" was used.
 */
const destinationOf = (payout) => {
    const d = payout.paymentDetails || {};
    if (payout.paymentMethod === 'mobile_money') {
        const parts = [d.mobileMoneyProvider, d.mobileMoneyNumber].filter(Boolean);
        return parts.join(' ');
    }
    if (payout.paymentMethod === 'bank_transfer') {
        const parts = [d.bankName, d.accountNumber].filter(Boolean);
        return parts.join(' ');
    }
    return '';
};

const METHOD_LABELS = {
    bank_transfer: 'Bank transfer',
    mobile_money: 'Mobile money',
    cheque: 'Cheque',
};

const th = {
    ...text.label,
    display: 'table-cell',
    textAlign: 'left',
    padding: '10px 14px',
    borderBottom: `1px solid ${colors.line}`,
    marginBottom: 0,
    whiteSpace: 'nowrap',
};

const td = {
    ...text.body,
    padding: '12px 14px',
    borderBottom: `1px solid ${colors.lineSoft}`,
    verticalAlign: 'top',
};

const PartnerPayouts = () => {
    const { agent } = useSelector((state) => state.auth);
    const user = agent || getUserFromLocalStorage();
    const partnerId = user?.result?.partnerId || user?.result?._id;

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [payouts, setPayouts] = useState([]);
    const [summary, setSummary] = useState(null);
    const [pagination, setPagination] = useState(null);
    const [status, setStatus] = useState('');
    const [page, setPage] = useState(1);

    const load = useCallback(async () => {
        if (!partnerId) {
            setLoading(false);
            setError('No partner account is linked to this login, so payouts cannot be loaded.');
            return;
        }
        setLoading(true);
        setError('');
        try {
            const { data } = await apiFetchPartnerPayouts(partnerId, {
                status: status || undefined,
                page,
                limit: PAGE_SIZE,
            });
            const payload = data?.data || {};
            setPayouts(payload.payouts || []);
            setSummary(payload.summary || null);
            setPagination(payload.pagination || null);
        } catch (err) {
            setError(
                err?.response?.data?.error?.message ||
                'Could not load payouts. Please try again.'
            );
            setPayouts([]);
            setSummary(null);
            setPagination(null);
        } finally {
            setLoading(false);
        }
    }, [partnerId, status, page]);

    useEffect(() => {
        load();
    }, [load]);

    const totalCount = pagination?.totalPayouts || 0;
    const totalPages = pagination?.totalPages || 1;
    const currentPage = pagination?.currentPage || page;

    /**
     * The server sums the summary amount over a key that the payout documents
     * themselves may not carry, so a partner with real completed payouts can
     * still be handed a zero here. Saying nothing is measured beats printing a
     * zero that reads like "you have been paid nothing".
     */
    const amountLooksUnreported = (summary?.totalPayouts || 0) > 0 && !summary?.totalAmount;

    const cards = summary
        ? [
            { label: 'Payouts issued', value: String(summary.totalPayouts || 0) },
            {
                label: 'Total amount',
                value: amountLooksUnreported ? 'Not reported' : ugx(summary.totalAmount),
            },
            { label: 'Pending', value: String(summary.pendingPayouts || 0) },
            { label: 'Completed', value: String(summary.completedPayouts || 0) },
        ]
        : [];

    return (
        <div className="content" style={{ fontFamily: font.sans, background: colors.ground }}>
            <div style={{ marginBottom: 20 }}>
                <h4 style={text.title}>
                    Payouts
                    <PartnerHelp topic="earningsVsPayouts" />
                </h4>
                <p style={text.subtitle}>
                    What Lusuku has actually paid you, what is still on its way, and when each
                    payment moved.
                </p>
            </div>

            {error && (
                <div
                    style={{
                        ...card,
                        border: `1px solid ${colors.red}`,
                        background: colors.redSoft,
                    }}
                >
                    <div style={{ ...cardBody, ...text.body, color: colors.red }}>{error}</div>
                </div>
            )}

            {summary && (
                <div style={{ ...grid(4, 16), marginBottom: 16 }}>
                    {cards.map((c) => (
                        <div key={c.label} style={{ ...card, marginBottom: 0 }}>
                            <div style={{ padding: '18px 20px' }}>
                                <div style={{ ...text.label, marginBottom: 8 }}>{c.label}</div>
                                <div
                                    style={{
                                        ...text.numeric,
                                        fontSize: 20,
                                        fontWeight: 700,
                                        color: c.value === 'Not reported' ? colors.faint : colors.ink,
                                    }}
                                >
                                    {c.value}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <div style={card}>
                <div style={cardBody}>
                    <div
                        style={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            alignItems: 'flex-end',
                            gap: 16,
                            marginBottom: 20,
                        }}
                    >
                        <div style={{ minWidth: 200 }}>
                            <label style={text.label} htmlFor="payout-status">
                                Status
                                <PartnerHelp topic="payoutStatus" />
                            </label>
                            <AppSelect
                                id="payout-status"
                                value={status}
                                // A new filter is a new result set, so the old page number
                                // would land the partner on an empty page.
                                onChange={(e) => { setStatus(e.target.value); setPage(1); }}
                                {...inp({ width: 220 })}
                            >
                                {STATUSES.map((s) => (
                                    <option key={s.value || 'all'} value={s.value}>{s.label}</option>
                                ))}
                            </AppSelect>
                        </div>
                        <div style={{ ...text.help, marginBottom: 10 }}>
                            {totalCount} {totalCount === 1 ? 'payout' : 'payouts'} match this filter
                        </div>
                    </div>

                    {loading && <p style={text.body}>Loading payouts...</p>}

                    {!loading && !error && payouts.length === 0 && (
                        <div style={{ padding: '40px 0', textAlign: 'center' }}>
                            <p style={{ ...text.body, fontWeight: 600, color: colors.ink }}>
                                No payouts have been issued yet.
                            </p>
                            <p style={{ ...text.body, color: colors.muted, maxWidth: 460, margin: '8px auto 0' }}>
                                Earnings become a payout once a payout run completes for the
                                period. Until then your work shows on the Earnings screen and
                                nothing appears here.
                            </p>
                        </div>
                    )}

                    {!loading && payouts.length > 0 && (
                        // Payout rows carry a reference, a period and an amount, so the row is
                        // wider than a phone. Scroll the table, never the page.
                        <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 860 }}>
                                <thead>
                                    <tr>
                                        <th style={th}>Date</th>
                                        <th style={th}>Reference</th>
                                        <th style={th}>Period</th>
                                        <th style={th}>Orders</th>
                                        <th style={th}>Amount</th>
                                        <th style={th}>Paid to</th>
                                        <th style={th}>Status</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {payouts.map((p) => {
                                        const destination = destinationOf(p);
                                        return (
                                            <tr key={p._id}>
                                                <td style={td}>
                                                    {/* paidAt is the date the partner cares about; before
                                                        payment there is only the date it was raised. */}
                                                    <div style={text.numeric}>
                                                        {formatDate(p.paidAt || p.createdAt)}
                                                    </div>
                                                    {!p.paidAt && (
                                                        <div style={text.help}>Raised, not yet paid</div>
                                                    )}
                                                </td>
                                                <td style={{ ...td, fontFamily: font.mono, fontSize: 13 }}>
                                                    <div>{p.batchId || p._id}</div>
                                                    {p.transactionReference && (
                                                        <div style={{ ...text.help, fontFamily: font.mono }}>
                                                            {p.transactionReference}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={{ ...td, whiteSpace: 'nowrap' }}>
                                                    <div style={text.numeric}>{formatDate(p.periodStart)}</div>
                                                    <div style={text.help}>to {formatDate(p.periodEnd)}</div>
                                                </td>
                                                <td style={{ ...td, ...text.numeric }}>{p.totalOrders || 0}</td>
                                                <td style={td}>
                                                    <div style={{ ...text.numeric, fontWeight: 700 }}>
                                                        {ugx(p.netPayout)}
                                                    </div>
                                                    {(p.bonuses > 0 || p.penalties > 0 || p.adjustments !== 0) && (
                                                        <div style={text.help}>
                                                            Earnings {ugx(p.partnerEarnings)}
                                                            {p.bonuses > 0 ? `, bonuses ${ugx(p.bonuses)}` : ''}
                                                            {p.penalties > 0 ? `, penalties ${ugx(p.penalties)}` : ''}
                                                            {p.adjustments ? `, adjustments ${ugx(p.adjustments)}` : ''}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    <div style={{ ...text.body, color: colors.body }}>
                                                        {METHOD_LABELS[p.paymentMethod] || 'Not set'}
                                                    </div>
                                                    {destination && (
                                                        <div style={{ ...text.help, fontFamily: font.mono }}>
                                                            {destination}
                                                        </div>
                                                    )}
                                                </td>
                                                <td style={td}>
                                                    <span style={pill(statusTone(p.status))}>
                                                        {p.status || 'unknown'}
                                                    </span>
                                                    {p.status === 'failed' && p.failureReason && (
                                                        <div style={{ ...text.help, color: colors.red }}>
                                                            {p.failureReason}
                                                        </div>
                                                    )}
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}

                    {!loading && totalCount > PAGE_SIZE && (
                        <div
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                gap: 12,
                                marginTop: 20,
                            }}
                        >
                            <button
                                type="button"
                                style={button('secondary', currentPage <= 1 ? { opacity: 0.5, cursor: 'not-allowed' } : {})}
                                disabled={currentPage <= 1}
                                onClick={() => setPage((p) => Math.max(1, p - 1))}
                            >
                                Previous
                            </button>
                            <span style={{ ...text.help, margin: 0 }}>
                                Page {currentPage} of {totalPages}
                            </span>
                            <button
                                type="button"
                                style={button('secondary', currentPage >= totalPages ? { opacity: 0.5, cursor: 'not-allowed' } : {})}
                                disabled={currentPage >= totalPages}
                                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            >
                                Next
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerPayouts;
